import {
  ActionableNotification,
  InlineLoading,
  InlineNotification,
  Tag,
} from '@carbon/react';

import type { Row } from '@tanstack/react-table';

import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link';
import { formatNumber } from 'utils/number';
import {
  subtractCoverageLabel,
  subtractCoverageSuffix,
} from 'utils/subtract-coverage';

import type { AssetListSummary, AssetNameCheck } from '../asset-list-utils';
import { MAX_OVERFLOW_NAMES } from '../source-tag-set';
import type { AssetListTract, AssetNameResult } from '../types';
import { PREVIEW_TABLE_CONFIG } from '../utils';
import styles from './asset-list-results.module.scss';

/**
 * A count with separators, as every number on screen is shown.
 *
 * @param {number} value - The count
 * @return {string} The count, formatted
 */
const count = (value: number) => formatNumber({ value, minPrecision: 0 });

/**
 * An unmatched name as the warning lists it: verbatim, and an ambiguous one
 * with how many Tracts share it.
 *
 * @param {AssetNameResult} result - A not-found or ambiguous result
 * @return {string} The name as listed in the warning
 */
const unmatchedLabel = (result: AssetNameResult) =>
  result.status === 'ambiguous'
    ? `${result.name} (matches ${count(result.matchCount)} assets)`
    : result.name;

/**
 * The unmatched names as the warning lists them: the first
 * `MAX_OVERFLOW_NAMES`, then how many more, as the source tags sum them up.
 *
 * @param {AssetNameResult[]} unmatched - The not-found and ambiguous results
 * @return {string} The names, joined
 */
const unmatchedList = (unmatched: AssetNameResult[]) => {
  const named = unmatched
    .slice(0, MAX_OVERFLOW_NAMES)
    .map(unmatchedLabel)
    .join(', ');
  const rest = unmatched.length - MAX_OVERFLOW_NAMES;
  return rest > 0 ? `${named} and ${count(rest)} more` : named;
};

const TEXT = { type: 'text' as const };

// `meta.type` set on every value column so a missing value reads as an em dash.
const PREVIEW_COLUMNS: DataTableColumnDef<AssetListTract>[] = [
  // Opens the Tract (or a nested Subtract) in a new tab, as the Step 1 and evaluation Tracts
  // tables do. A Tract held in part says how much of it the list holds.
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    meta: TEXT,
    cell: ({ row }) => {
      const { salesforceId, name, subtracts, subtractCount } = row.original;
      const label = subtractCoverageLabel(subtracts.length, subtractCount);
      return (
        <>
          <Link
            to={`/crm/tracts_and_subtracts/${encodeURIComponent(
              salesforceId
            )}/edit`}
            openInNewTab
          >
            {name}
          </Link>
          {subtractCoverageSuffix(label)}
        </>
      );
    },
  },
  // "; " as on Step 1 and in its export: an owner's name can hold a comma.
  {
    id: 'entity',
    header: 'Entity',
    cell: ({ row }) =>
      row.original.entities
        .map((entity) => entity.name)
        .filter(Boolean)
        .join('; ') || '—',
  },
  // The value the mock calls Interest Type, under the wizard's own label for it.
  {
    id: 'instrumentType',
    accessorKey: 'instrumentType',
    header: 'Instrument Type',
    meta: TEXT,
  },
  {
    id: 'surveyArea',
    accessorKey: 'surveyArea',
    header: 'Location',
    meta: TEXT,
  },
  {
    id: 'netAcres',
    header: 'Net Acres',
    meta: { type: 'decimal' as const },
    cell: ({ row }) => {
      const { netAcres, netAcreageUnits } = row.original;
      if (netAcres === null) return '—';
      const acres = formatNumber({
        value: netAcres,
        minPrecision: 0,
        maxPrecision: 2,
      });
      return netAcreageUnits ? `${acres} ${netAcreageUnits}` : acres;
    },
  },
  {
    id: 'grossAcres',
    accessorKey: 'grossAcres',
    header: 'Gross Acres',
    meta: { type: 'decimal' as const },
  },
  { id: 'depth', accessorKey: 'depth', header: 'Depth', meta: TEXT },
];

/**
 * The listed Subtracts of a Tract the list holds in part, under its row, in the
 * preview's own columns: each takes its owners and Instrument Type from its
 * Tract, as Step 1's Subtract rows do.
 *
 * @param {Row<AssetListTract>} row - The Tract's row
 * @return {JSX.Element} The Subtract rows
 */
const renderListedSubtracts = (row: Row<AssetListTract>) => (
  <div className={styles.subtracts}>
    <DataTable<AssetListTract>
      data={row.original.subtracts.map((subtract) => ({
        ...row.original,
        ...subtract,
        surveyArea: null,
        depth: null,
        subtractCount: null,
        subtracts: [],
      }))}
      columns={PREVIEW_COLUMNS}
      getRowId={(subtract) => subtract.salesforceId}
      config={{ ...PREVIEW_TABLE_CONFIG, hideHeader: true }}
    />
  </div>
);

/**
 * Whether a preview row has listed Subtracts to expand to: a Tract held in part.
 *
 * @param {AssetListTract} tract - A preview row
 * @return {boolean} True for a Tract held in part
 */
const canListSubtracts = (tract: AssetListTract) => tract.subtracts.length > 0;

/** Props for {@link AssetListResults}. */
export interface AssetListResultsProps {
  summary: AssetListSummary;
  failure: AssetNameCheck['failure'];
  /** Re-sends the names still without an answer. */
  onTryAgain: () => void;
}

/**
 * What the check made of the asset list, layer by layer: the check under way
 * (or failed, with Try again), the matched and not-found counts, a warning
 * naming the unmatched names — which never blocks Next — and the Matched
 * Assets preview, one row per contributed Tract in list order, a Tract held in
 * part expanding to its listed Subtracts. Its count is of assets: 1 per whole
 * Tract, 1 per listed Subtract of one held in part. The preview scrolls inside a
 * fixed height and draws only the rows in view, so a list of any length fits.
 *
 * @param {AssetListResultsProps} props - Summary, failure and retry
 * @return {JSX.Element} The result layers
 */
export function AssetListResults({
  summary,
  failure,
  onTryAgain,
}: AssetListResultsProps) {
  const { matched, unmatched, unchecked, tracts, assetCount } = summary;
  const checking = unchecked.length > 0;
  const hasResults = matched.length > 0 || unmatched.length > 0;
  const one = unmatched.length === 1;

  return (
    <div className={styles.results}>
      {checking && failure !== null && (
        // `alert`, not the default `alertdialog`: Carbon traps focus in an
        // alertdialog, which would keep the user out of the paste box.
        <ActionableNotification
          inline
          role="alert"
          kind="error"
          lowContrast
          hideCloseButton
          title="Names couldn't be validated."
          subtitle={failure.reason ?? undefined}
          actionButtonLabel="Try again"
          onActionButtonClick={onTryAgain}
        />
      )}
      {checking && failure === null && (
        <InlineLoading description="Validating against Insights asset lookup…" />
      )}

      {hasResults && (
        <>
          <div className={styles.counts}>
            <Tag type="green">{count(matched.length)} matched in Insights</Tag>
            {unmatched.length > 0 && (
              <Tag type="red">{count(unmatched.length)} not found</Tag>
            )}
          </div>

          {unmatched.length > 0 && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              className={styles.warning}
              title={`${count(unmatched.length)} ${
                one ? 'name' : 'names'
              } not found in Insights`}
              subtitle={`${unmatchedList(unmatched)}. Check for typos or remove ${
                one ? 'it' : 'them'
              } from the list.`}
            />
          )}

          {tracts.length > 0 && (
            <section className={styles.preview}>
              <h4 className={styles.previewTitle}>
                Matched Assets ({count(assetCount)})
              </h4>
              <DataTable<AssetListTract>
                data={tracts}
                columns={PREVIEW_COLUMNS}
                getRowId={(tract) => tract.salesforceId}
                maxBodyHeight="20rem"
                config={PREVIEW_TABLE_CONFIG}
                // Only a list holding a Tract in part has rows to expand.
                {...(tracts.some(canListSubtracts) && {
                  renderRowSubComponent: renderListedSubtracts,
                  getRowCanExpand: (row: Row<AssetListTract>) =>
                    canListSubtracts(row.original),
                })}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}
